import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Wifi, Zap, Database, Server, ArrowRight, AlertTriangle, CheckCircle2,
  Clock, FileText, Lock, MessageCircle, X, Send,
} from "lucide-react";
import { Button } from "../../components/ui/button";

// ─────────────────────────────────────────────────────────────────────────────
// PREVIEW: Real-time messaging upgrade
// Route: /preview/messaging-realtime
// Public, non-destructive — explains the 3 transport options and lets you
// preview what each looks like in the chat thread (typing indicator, presence,
// instant delivery, read receipts).
// ─────────────────────────────────────────────────────────────────────────────
const OPTIONS = [
  {
    id: "polling",
    label: "Today: HTTP polling",
    color: "border-foreground/15",
    badge: "Currently live",
    badgeTone: "bg-foreground/10 text-foreground/70",
    summary: "setInterval every 8s pulls /api/chat/threads/{id}/messages with ?since=<timestamp>.",
    pros: [
      "Already implemented — zero infra changes",
      "Plays nicely with our k8s ingress (no sticky sessions)",
      "Works on every browser including locked-down corporate networks",
    ],
    cons: [
      "Up to 8s delay before a buyer sees a seller's reply (or vice versa)",
      "No typing indicator possible",
      "No presence (online/offline) possible",
      "Burns bandwidth even on idle threads",
    ],
    infraNeeded: "None.",
    cost: "Already paid for.",
  },
  {
    id: "sse",
    label: "Recommended: Server-Sent Events (SSE)",
    color: "border-foreground bg-accent/5",
    badge: "Recommended",
    badgeTone: "bg-foreground text-background",
    summary: "FastAPI EventSourceResponse streams new messages over a long-lived HTTP/1.1 connection, push-style.",
    pros: [
      "Sub-100ms delivery — feels instant",
      "Plain HTTPS, no protocol upgrade — works behind k8s ingress as-is",
      "Only ~120 LoC backend + ~30 LoC frontend (EventSource is built into every browser)",
      "Auto-reconnect with last-event-id is part of the EventSource spec",
      "Can power typing indicators + presence on the same channel",
    ],
    cons: [
      "One open connection per active tab (k8s ingress max-conn already supports this)",
      "Unidirectional (server→client) — but messages still go via existing POST endpoint, so this is fine",
    ],
    infraNeeded: "None new. Same FastAPI process, same Mongo, same Atlas (Atlas is just our managed Mongo host — it's not a transport layer).",
    cost: "Zero additional vendor cost.",
  },
  {
    id: "ws",
    label: "Alternative: WebSockets",
    color: "border-foreground/15",
    badge: "Overkill for now",
    badgeTone: "bg-foreground/10 text-foreground/70",
    summary: "Full-duplex socket — same speed as SSE but bidirectional.",
    pros: [
      "Same sub-100ms delivery as SSE",
      "Useful if we ever want voice/video or screen-share later",
    ],
    cons: [
      "Requires k8s ingress upgrade for sticky sessions OR a Redis pub/sub layer for fan-out across pod replicas",
      "More moving parts, heavier connection, harder to debug",
      "Total overkill for text chat",
    ],
    infraNeeded: "Redis pub/sub for multi-pod fan-out (~$15/mo Upstash) + ingress sticky-session config.",
    cost: "~$15/mo Redis.",
  },
  {
    id: "vendor",
    label: "Alternative: Pusher / Ably / Atlas Triggers",
    color: "border-foreground/15",
    badge: "Vendor lock-in",
    badgeTone: "bg-foreground/10 text-foreground/70",
    summary: "Outsource the realtime channel to a hosted service.",
    pros: [
      "Zero infra to maintain",
      "Built-in presence, typing, reconnect",
    ],
    cons: [
      "$50–$200+/mo at our scale",
      "Vendor lock-in for what's essentially 200 LoC of SSE",
      "Atlas Change Streams require a long-lived TCP cursor — same scaling issue as WebSockets without solving anything",
    ],
    infraNeeded: "Pusher / Ably account.",
    cost: "$50–$200+/mo.",
  },
];

export default function MessagingRealtimePreview() {
  const [active, setActive] = useState("sse");
  const opt = OPTIONS.find((o) => o.id === active);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-background/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-md bg-foreground text-background grid place-items-center font-bold">T</div>
            <span className="display font-semibold">TRAQO Preview</span>
          </Link>
          <Link to="/seller/messages" className="text-sm text-foreground/60 hover:text-foreground">
            Live page →
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="text-xs uppercase tracking-widest text-foreground/50 mb-2">
          Audit response · realtime messaging
        </div>
        <h1 className="display text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05]">
          What tool keeps Messages live?
        </h1>
        <p className="mt-4 text-foreground/70 max-w-2xl leading-relaxed">
          Quick context on what's currently shipped, why it isn't truly instant, and the 4 options
          for upgrading. Atlas is mentioned a lot — but Atlas is our managed-Mongo host,
          not a realtime transport. The choice is really between <strong>polling</strong>,{" "}
          <strong>Server-Sent Events</strong>, <strong>WebSockets</strong>, and{" "}
          <strong>vendor</strong>.
        </p>

        {/* Important fact box */}
        <div className="mt-8 rounded-xl border border-border bg-foreground/[0.02] p-5 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-foreground/60 mt-0.5 shrink-0" />
          <div className="text-sm leading-relaxed text-foreground/75">
            <strong className="text-foreground">Heads-up on the original "bug":</strong> when you
            saw every message bubble showing "Apex Wholesale LLC", that wasn't a sender-attribution
            bug. The chat code already differentiates correctly via{" "}
            <code className="text-xs bg-foreground/10 px-1 py-0.5 rounded">
              m.author_user_id === me.id
            </code>{" "}
            — the seller's bubbles align right (dark), the buyer's align left (white). What you saw
            is a one-sided thread: the buyer sent every message and GlowCo never replied. Send one
            seller-side reply and you'll immediately see two-tone alignment.
          </div>
        </div>

        {/* Option selector */}
        <div className="mt-10 grid md:grid-cols-2 gap-3">
          {OPTIONS.map((o) => (
            <button
              key={o.id}
              onClick={() => setActive(o.id)}
              data-testid={`opt-${o.id}`}
              className={`text-left rounded-xl border-2 p-5 transition-all ${
                active === o.id
                  ? "border-foreground bg-accent/5"
                  : "border-border bg-background hover:border-foreground/30"
              }`}
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="text-sm font-medium">{o.label}</div>
                <span
                  className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${o.badgeTone}`}
                >
                  {o.badge}
                </span>
              </div>
              <p className="text-xs text-foreground/65 leading-relaxed">{o.summary}</p>
            </button>
          ))}
        </div>

        {/* Active option detail */}
        <section className="mt-10 grid lg:grid-cols-[1fr_440px] gap-6 items-start">
          <div className="rounded-2xl border-2 border-foreground bg-background p-6 sm:p-8">
            <div className="text-xs uppercase tracking-widest text-foreground/50 mb-1">
              Selected
            </div>
            <h2 className="display text-2xl font-semibold mb-5">{opt.label}</h2>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#0d8f4a] mb-2 flex items-center gap-1.5">
                  <CheckCircle2 className="w-3 h-3" />
                  Pros
                </div>
                <ul className="space-y-1.5 text-sm text-foreground/75">
                  {opt.pros.map((p, i) => (
                    <li key={i} className="flex gap-1.5">
                      <span className="text-[#0d8f4a]">+</span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-foreground/60 mb-2 flex items-center gap-1.5">
                  <X className="w-3 h-3" />
                  Cons
                </div>
                <ul className="space-y-1.5 text-sm text-foreground/75">
                  {opt.cons.map((c, i) => (
                    <li key={i} className="flex gap-1.5">
                      <span className="text-foreground/40">−</span>
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3 text-xs">
              <div className="rounded-lg bg-foreground/[0.04] px-3 py-2">
                <div className="text-foreground/50 uppercase tracking-widest text-[10px] mb-0.5">
                  Infra needed
                </div>
                <div className="text-foreground/85">{opt.infraNeeded}</div>
              </div>
              <div className="rounded-lg bg-foreground/[0.04] px-3 py-2">
                <div className="text-foreground/50 uppercase tracking-widest text-[10px] mb-0.5">
                  Monthly cost
                </div>
                <div className="text-foreground/85">{opt.cost}</div>
              </div>
            </div>
          </div>

          {/* Mock chat showing the upgrade benefits */}
          <aside className="rounded-2xl border border-border bg-foreground/[0.02] p-5 sticky top-24">
            <div className="text-xs uppercase tracking-widest text-foreground/50 mb-3">
              What it feels like with {opt.label.split(":").slice(-1)[0].trim()}
            </div>
            <div className="rounded-xl bg-white border border-border overflow-hidden">
              <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-foreground text-accent grid place-items-center text-xs font-semibold">
                  AW
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium">Apex Wholesale LLC</div>
                  {(active === "sse" || active === "ws" || active === "vendor") && (
                    <div className="text-[10px] text-[#0d8f4a] flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#0d8f4a]" />
                      online
                    </div>
                  )}
                  {active === "polling" && (
                    <div className="text-[10px] text-foreground/40">last seen ~2m ago</div>
                  )}
                </div>
              </div>
              <div className="p-4 space-y-2 bg-[#fafaf7] min-h-[260px]">
                <div className="flex justify-start">
                  <div className="max-w-[78%] rounded-2xl rounded-tl-sm px-3 py-2 text-[13px] bg-white border border-border">
                    Hey, can we move PO #4421 up by a week?
                  </div>
                </div>
                <div className="flex justify-end">
                  <div className="max-w-[78%] rounded-2xl rounded-tr-sm px-3 py-2 text-[13px] bg-foreground text-background">
                    Checking warehouse — give me 1m.
                  </div>
                </div>
                {(active === "sse" || active === "ws" || active === "vendor") && (
                  <div className="flex justify-start">
                    <div className="rounded-2xl rounded-tl-sm px-3 py-2 text-[12px] bg-white border border-border text-foreground/55 italic flex items-center gap-1.5">
                      <span className="flex gap-0.5">
                        <span className="w-1 h-1 rounded-full bg-foreground/50 animate-bounce" />
                        <span className="w-1 h-1 rounded-full bg-foreground/50 animate-bounce [animation-delay:120ms]" />
                        <span className="w-1 h-1 rounded-full bg-foreground/50 animate-bounce [animation-delay:240ms]" />
                      </span>
                      typing…
                    </div>
                  </div>
                )}
                {active === "polling" && (
                  <div className="flex justify-center pt-3">
                    <div className="text-[10px] text-foreground/40 italic">
                      checking for new messages every 8s
                    </div>
                  </div>
                )}
              </div>
              <div className="border-t border-border p-2 flex gap-2 items-center">
                <input
                  className="flex-1 text-[13px] px-2 py-1.5 outline-none"
                  placeholder="Type a message…"
                  disabled
                />
                <Button size="sm" disabled className="rounded-full bg-foreground text-background h-8 px-3">
                  <Send className="w-3 h-3 mr-1" />Send
                </Button>
              </div>
            </div>
            <div className="mt-3 text-[11px] text-foreground/55 leading-relaxed">
              {active === "polling" &&
                "Today: bubbles arrive on the next 8-second tick. No typing indicator. No presence."}
              {active === "sse" &&
                "Recommended: bubbles arrive ~80ms after the other side hits Send. Typing dots appear in real-time. Online/offline dot is live."}
              {active === "ws" &&
                "Same UX as SSE, but pays for full-duplex we don't actually need."}
              {active === "vendor" &&
                "Same UX as SSE, but $50-$200/mo for what we can build in 200 LoC."}
            </div>
          </aside>
        </section>

        {/* Recommendation panel */}
        <section className="mt-12 rounded-2xl border-2 border-foreground bg-accent/[0.06] p-6 sm:p-8">
          <div className="text-xs uppercase tracking-widest text-foreground/60 mb-2 flex items-center gap-1.5">
            <Zap className="w-3 h-3" />
            My recommendation
          </div>
          <h2 className="display text-2xl font-semibold mb-3">
            Ship Server-Sent Events (SSE).
          </h2>
          <p className="text-foreground/75 leading-relaxed max-w-3xl">
            Cheapest option that gives true instant delivery + typing + presence. Zero new vendors.
            Works behind our k8s ingress with no config changes. Built into every browser via{" "}
            <code className="text-xs bg-foreground/10 px-1 py-0.5 rounded">EventSource</code>. Total
            implementation: <strong>~150 LoC backend + ~50 LoC frontend</strong>, ships in one turn.
            Polling stays as automatic fallback if the SSE channel drops.
          </p>

          <div className="mt-6 grid sm:grid-cols-3 gap-3">
            {[
              { icon: Server, t: "Backend", s: "FastAPI EventSourceResponse · per-thread async generator · Mongo change watch via tailable cursor on a `chat_events` capped collection" },
              { icon: Wifi, t: "Frontend", s: "EventSource per active thread · auto-reconnect with last-event-id · falls back to polling when SSE returns 503/disconnects 3+ times" },
              { icon: Lock, t: "Auth", s: "JWT in URL token param (SSE can't set headers in the browser) — short-lived 5min channel JWTs minted via /api/chat/sse-token" },
            ].map((c, i) => (
              <div key={i} className="rounded-lg border border-border bg-background p-4">
                <c.icon className="w-4 h-4 text-foreground/60 mb-2" />
                <div className="text-sm font-medium">{c.t}</div>
                <div className="text-[12px] text-foreground/65 mt-1 leading-relaxed">{c.s}</div>
              </div>
            ))}
          </div>
        </section>

        <div className="mt-12 flex items-center justify-between border-t border-border pt-6">
          <Link to="/" className="text-sm text-foreground/60 hover:text-foreground">
            ← Back to home
          </Link>
          <span className="text-xs text-foreground/50">Preview only · no code shipped</span>
        </div>
      </main>
    </div>
  );
}
